// SPDX-License-Identifier: AGPL-3.0-or-later

import {normalizeViewerStreamKeys} from '@app/features/voice/engine/VoiceStreamWatchStateMachine';
import {areOrderedStringArraysEqual} from '@app/features/voice/utils/StringArrayUtils';
import {assign, getInitialSnapshot, type SnapshotFrom, setup, transition} from 'xstate';

export interface VoiceStateSyncPayload {
	guild_id: string | null;
	channel_id: string;
	connection_id: string;
	self_mute: boolean;
	self_deaf: boolean;
	self_video: boolean;
	self_stream: boolean;
	viewer_stream_keys: ReadonlyArray<string>;
}

export type VoiceStateSyncPartial = Partial<
	Pick<VoiceStateSyncPayload, 'self_video' | 'self_stream' | 'self_mute' | 'self_deaf' | 'viewer_stream_keys'>
>;

export type VoiceStateSyncCommand =
	| {type: 'scheduleFlush'}
	| {type: 'send'; payload: VoiceStateSyncPayload}
	| {type: 'scheduleInFlightTimeout'}
	| {type: 'clearInFlightTimeout'};

interface VoiceStateSyncContext {
	pending: VoiceStateSyncPayload | null;
	lastSent: VoiceStateSyncPayload | null;
	inFlight: VoiceStateSyncPayload | null;
	serverState: VoiceStateSyncPayload | null;
	flushScheduled: boolean;
	inFlightTimeoutScheduled: boolean;
	commands: ReadonlyArray<VoiceStateSyncCommand>;
}

export type VoiceStateSyncEvent =
	| {type: 'sync.request'; payload: VoiceStateSyncPayload}
	| {type: 'sync.confirm'; payload: VoiceStateSyncPayload | null}
	| {type: 'sync.flush'}
	| {type: 'sync.inFlightTimeout'}
	| {type: 'sync.reset'}
	| {type: 'sync.clearCommands'};

const EMPTY_COMMANDS: ReadonlyArray<VoiceStateSyncCommand> = [];

function initialContext(commands: ReadonlyArray<VoiceStateSyncCommand> = EMPTY_COMMANDS): VoiceStateSyncContext {
	return {
		pending: null,
		lastSent: null,
		inFlight: null,
		serverState: null,
		flushScheduled: false,
		inFlightTimeoutScheduled: false,
		commands,
	};
}

export function normalizeVoiceStateSyncPayload(payload: VoiceStateSyncPayload): VoiceStateSyncPayload {
	return {
		...payload,
		guild_id: payload.guild_id ?? null,
		viewer_stream_keys: normalizeViewerStreamKeys(payload.viewer_stream_keys),
	};
}

export function isSameVoiceStateSyncContext(a: VoiceStateSyncPayload, b: VoiceStateSyncPayload): boolean {
	return a.guild_id === b.guild_id && a.channel_id === b.channel_id && a.connection_id === b.connection_id;
}

export function areVoiceStateSyncPayloadsEqual(a: VoiceStateSyncPayload, b: VoiceStateSyncPayload): boolean {
	return (
		isSameVoiceStateSyncContext(a, b) &&
		a.self_mute === b.self_mute &&
		a.self_deaf === b.self_deaf &&
		a.self_video === b.self_video &&
		a.self_stream === b.self_stream &&
		areOrderedStringArraysEqual(a.viewer_stream_keys, b.viewer_stream_keys)
	);
}

function appendCommand(context: VoiceStateSyncContext, command: VoiceStateSyncCommand): VoiceStateSyncContext {
	return {
		...context,
		commands: [...context.commands, command],
	};
}

function scheduleFlush(context: VoiceStateSyncContext): VoiceStateSyncContext {
	if (context.flushScheduled) return context;
	return appendCommand({...context, flushScheduled: true}, {type: 'scheduleFlush'});
}

function scheduleInFlightTimeout(context: VoiceStateSyncContext): VoiceStateSyncContext {
	if (!context.inFlight || context.inFlightTimeoutScheduled) return context;
	return appendCommand({...context, inFlightTimeoutScheduled: true}, {type: 'scheduleInFlightTimeout'});
}

function clearInFlightTimeout(context: VoiceStateSyncContext): VoiceStateSyncContext {
	if (!context.inFlightTimeoutScheduled) return context;
	return appendCommand({...context, inFlightTimeoutScheduled: false}, {type: 'clearInFlightTimeout'});
}

function requestState(context: VoiceStateSyncContext, payload: VoiceStateSyncPayload): VoiceStateSyncContext {
	return scheduleFlush({
		...context,
		pending: normalizeVoiceStateSyncPayload(payload),
	});
}

function confirmServerState(
	context: VoiceStateSyncContext,
	payload: VoiceStateSyncPayload | null,
): VoiceStateSyncContext {
	if (!payload) {
		return {
			...context,
			serverState: null,
		};
	}
	const serverState = normalizeVoiceStateSyncPayload(payload);
	let next: VoiceStateSyncContext = {
		...context,
		serverState,
	};
	if (next.inFlight && areVoiceStateSyncPayloadsEqual(next.inFlight, serverState)) {
		next = clearInFlightTimeout({
			...next,
			inFlight: null,
			lastSent: serverState,
		});
	}
	if (next.pending && areVoiceStateSyncPayloadsEqual(next.pending, serverState)) {
		next = {
			...next,
			pending: null,
		};
	}
	return scheduleFlush(next);
}

function flushState(context: VoiceStateSyncContext): VoiceStateSyncContext {
	let next: VoiceStateSyncContext = {
		...context,
		flushScheduled: false,
	};
	if (!next.pending) return next;
	const pending = next.pending;
	if (next.inFlight && !isSameVoiceStateSyncContext(next.inFlight, pending)) {
		next = clearInFlightTimeout({
			...next,
			inFlight: null,
		});
	}
	if (next.inFlight) {
		return scheduleInFlightTimeout(next);
	}
	if (next.lastSent && areVoiceStateSyncPayloadsEqual(next.lastSent, pending)) {
		return {
			...next,
			pending: null,
		};
	}
	if (next.serverState && areVoiceStateSyncPayloadsEqual(next.serverState, pending)) {
		return {
			...next,
			pending: null,
			lastSent: next.serverState,
		};
	}
	next = appendCommand(
		{
			...next,
			pending: null,
			lastSent: pending,
			inFlight: pending,
		},
		{type: 'send', payload: pending},
	);
	return scheduleInFlightTimeout(next);
}

function handleInFlightTimeout(context: VoiceStateSyncContext): VoiceStateSyncContext {
	let next: VoiceStateSyncContext = {
		...context,
		inFlightTimeoutScheduled: false,
	};
	if (!next.inFlight) return next;
	next = {
		...next,
		inFlight: null,
	};
	return scheduleFlush(next);
}

function resetState(context: VoiceStateSyncContext): VoiceStateSyncContext {
	return initialContext(context.inFlightTimeoutScheduled ? [{type: 'clearInFlightTimeout'}] : EMPTY_COMMANDS);
}

export const voiceStateSyncStateMachine = setup({
	types: {} as {
		context: VoiceStateSyncContext;
		events: VoiceStateSyncEvent;
	},
	actions: {
		request: assign(({context, event}) =>
			event.type === 'sync.request' ? requestState(context, event.payload) : context,
		),
		confirm: assign(({context, event}) =>
			event.type === 'sync.confirm' ? confirmServerState(context, event.payload) : context,
		),
		flush: assign(({context}) => flushState(context)),
		inFlightTimeout: assign(({context}) => handleInFlightTimeout(context)),
		reset: assign(({context}) => resetState(context)),
		clearCommands: assign(({context}) =>
			context.commands.length === 0 ? context : {...context, commands: EMPTY_COMMANDS},
		),
	},
}).createMachine({
	id: 'voiceStateSync',
	context: () => initialContext(),
	initial: 'tracking',
	states: {
		tracking: {
			on: {
				'sync.request': {actions: 'request'},
				'sync.confirm': {actions: 'confirm'},
				'sync.flush': {actions: 'flush'},
				'sync.inFlightTimeout': {actions: 'inFlightTimeout'},
				'sync.reset': {actions: 'reset'},
				'sync.clearCommands': {actions: 'clearCommands'},
			},
		},
	},
});

export type VoiceStateSyncSnapshot = SnapshotFrom<typeof voiceStateSyncStateMachine>;
export type VoiceStateSyncStateValue = 'idle' | 'pending' | 'waitingForServer';

export function createVoiceStateSyncSnapshot(): VoiceStateSyncSnapshot {
	return getInitialSnapshot(voiceStateSyncStateMachine);
}

export function transitionVoiceStateSyncSnapshot(
	snapshot: VoiceStateSyncSnapshot,
	event: VoiceStateSyncEvent,
): VoiceStateSyncSnapshot {
	return transition(voiceStateSyncStateMachine, snapshot, event)[0] as VoiceStateSyncSnapshot;
}

export function getVoiceStateSyncStateValue(snapshot: VoiceStateSyncSnapshot): VoiceStateSyncStateValue {
	if (snapshot.context.inFlight) return 'waitingForServer';
	if (snapshot.context.pending) return 'pending';
	return 'idle';
}

export function shouldApplyServerVoiceState(snapshot: VoiceStateSyncSnapshot, payload: VoiceStateSyncPayload): boolean {
	const latestLocalIntent = snapshot.context.pending ?? snapshot.context.inFlight;
	if (!latestLocalIntent) return true;
	const normalizedPayload = normalizeVoiceStateSyncPayload(payload);
	if (!isSameVoiceStateSyncContext(latestLocalIntent, normalizedPayload)) return true;
	return areVoiceStateSyncPayloadsEqual(latestLocalIntent, normalizedPayload);
}
